import React from 'react';

function PixelDetailsModal({ pixelData, onClose }) {
  if (!pixelData) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleVisitWebsite = () => {
    if (pixelData.website) {
      let url = pixelData.website;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleViewOnOpenSea = () => {
    // Generate OpenSea URL for the token
    const openseaUrl = `https://opensea.io/assets/ethereum/${pixelData.contractAddress}/${pixelData.tokenId}`;
    window.open(openseaUrl, '_blank', 'noopener,noreferrer');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        animation: 'modalFadeIn 0.2s ease-out'
      }}
      onClick={handleBackdropClick}
    >
      <style>
        {`
          @keyframes modalFadeIn {
            from {
              opacity: 0;
              transform: scale(0.9);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          @keyframes modalFadeOut {
            from {
              opacity: 1;
              transform: scale(1);
            }
            to {
              opacity: 0;
              transform: scale(0.9);
            }
          }
          .modal-content {
            animation: modalFadeIn 0.2s ease-out;
          }
        `}
      </style>
      
      <div 
        className="modal-content"
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '0',
          maxWidth: '400px', // More compact
          width: '95%',
          maxHeight: '80vh',
          overflow: 'auto',
          position: 'relative',
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.18)'
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: 'rgba(0, 0, 0, 0.08)',
            border: 'none',
            borderRadius: '50%',
            width: '28px',
            height: '28px',
            cursor: 'pointer',
            fontSize: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001
          }}
        >
          ×
        </button>

        {/* Image Section */}
        <div style={{
          padding: '18px 18px 10px',
          textAlign: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          {pixelData.image ? (
            <img 
              src={pixelData.image}
              alt={`Pixel #${pixelData.tokenId}`}
              style={{
                maxWidth: '90px', // Small preview
                maxHeight: '90px',
                objectFit: 'contain',
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            style={{
              display: pixelData.image ? 'none' : 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '90px',
              height: '90px',
              margin: '0 auto',
              background: 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)',
              backgroundSize: '20px 20px',
              backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
              borderRadius: '6px',
              border: '1.5px dashed #ccc',
              color: '#666',
              fontSize: '13px'
            }}
          >
            No Image
          </div>
        </div>

        {/* Info Section */}
        <div style={{ padding: '12px 18px' }}>
          <h2 style={{ 
            margin: '0 0 12px', 
            color: '#2E7D32',
            fontSize: '19px',
            textAlign: 'center'
          }}>
            {pixelData.name || `Pixel #${pixelData.tokenId}`}
          </h2>

          <div style={{ 
            display: 'grid', 
            gap: '10px',
            marginBottom: '16px'
          }}>
            {/* Token ID */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '7px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              fontSize: '14px'
            }}>
              <span style={{ fontWeight: 'bold', color: '#333' }}>Token ID:</span>
              {pixelData.size && parseInt(pixelData.size) > 1 ? (
                (() => {
                  const gridWidth = 100;
                  const startTokenId = parseInt(pixelData.tokenId);
                  const size = parseInt(pixelData.size);
                  const startRow = Math.floor((startTokenId - 1) / gridWidth);
                  const startCol = (startTokenId - 1) % gridWidth;
                  let endRow = startRow + size - 1;
                  let endCol = startCol + size - 1;
                  if (endRow > 99) endRow = 99;
                  if (endCol > 99) endCol = 99;
                  const endTokenId = (endRow * gridWidth + endCol + 1);
                  return (
                    <span style={{ color: '#666' }}>
                      #{startTokenId} - #{endTokenId}
                    </span>
                  );
                })()
              ) : (
                <span style={{ color: '#666' }}>#{pixelData.tokenId}</span>
              )}
            </div>

            {/* Owner */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '7px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              fontSize: '14px'
            }}>
              <span style={{ fontWeight: 'bold', color: '#333' }}>Owner:</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ color: '#666', fontFamily: 'monospace' }}>
                  {pixelData.owner.slice(0, 6)}...{pixelData.owner.slice(-4)}
                </span>
                <button
                  onClick={() => copyToClipboard(pixelData.owner)}
                  style={{
                    background: 'none',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                  title="Copy full address"
                >
                  📋
                </button>
              </div>
            </div>

            {/* Size */}
            {pixelData.size && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '7px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                fontSize: '14px'
              }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>Size:</span>
                <span style={{ color: '#666' }}>
                  {pixelData.size}×{pixelData.size} pixels
                </span>
              </div>
            )}

            {/* Website */}
            {pixelData.website && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '7px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                fontSize: '14px'
              }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>Website:</span>
                <a 
                  href={pixelData.website.startsWith('http') ? pixelData.website : `https://${pixelData.website}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#2196F3',
                    textDecoration: 'none',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  {pixelData.website.replace(/^https?:\/\//, '')}
                </a>
              </div>
            )}

            {/* Arweave Status */}
            {pixelData.image && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '7px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                fontSize: '14px'
              }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>Storage:</span>
                {pixelData.image.includes('arweave.net') ? (
                  <a 
                    href={pixelData.image}
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      color: '#4CAF50',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                  >
                    ✓ Arweave (Permanent)
                  </a>
                ) : (
                  <span style={{ 
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    Traditional Hosting
                  </span>
                )}
              </div>
            )}

            {/* Description */}
            {pixelData.description && (
              <div style={{
                padding: '10px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                borderLeft: '4px solid #2E7D32'
              }}>
                <span style={{ fontWeight: 'bold', color: '#333', display: 'block', marginBottom: '6px' }}>
                  Description:
                </span>
                <p style={{ margin: 0, color: '#666', lineHeight: '1.5' }}>
                  {pixelData.description}
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap'
          }}>
            {pixelData.website && (
              <button
                onClick={handleVisitWebsite}
                style={{
                  flex: 1,
                  padding: '10px 18px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#4CAF50'}
              >
                🌐 Visit Website
              </button>
            )}
            <button
              onClick={handleViewOnOpenSea}
              style={{
                flex: 1,
                padding: '10px 18px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#1976D2'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#2196F3'}
            >
              🔍 View on OpenSea
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PixelDetailsModal;